import { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, Settings, X, GripVertical } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PyramidItem, Category, TimeHorizon, StrategicDirection } from '../types';

// ============================================================================
// Types & Constants
// ============================================================================

interface PyramidViewProps {
  items: PyramidItem[];
  segments: Map<string, PyramidItem[]>;
  northStar?: string;
  categories: Category[];
  onAddItem: (name: string, description?: string, category?: Category, timeHorizon?: TimeHorizon) => void;
  onUpdateItem: (itemId: string, updates: Partial<PyramidItem>) => void;
  onDeleteItem: (itemId: string) => void;
  onMoveItem: (itemId: string, category: Category, timeHorizon: TimeHorizon, strategicDirection: StrategicDirection) => void;
  onUpdateRank: (itemId: string, newRank: number) => void;
  onViewMode: () => void;
  onEditMode: () => void;
  isViewMode: boolean;
  onUpdateCategories?: (categories: Category[]) => void;
}

const TIME_HORIZONS: TimeHorizon[] = ['Transform', 'Grow', 'Run'];

const TIME_HORIZON_LABELS: Record<TimeHorizon, string> = {
  Transform: 'Create the Future',
  Grow: 'Reinforce Foundation',
  Run: 'Get House in Order',
};

const TIME_HORIZON_DESCRIPTIONS: Record<TimeHorizon, string> = {
  Transform: 'Long-term strategic priorities (North Star)',
  Grow: 'Mid-term connecting priorities',
  Run: 'Near-term tactical priorities',
};

const TIME_HORIZON_COLORS: Record<TimeHorizon, { bg: string; border: string; text: string; dropzone: string }> = {
  Transform: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', dropzone: 'bg-[var(--slate-dim)]' },
  Grow: { bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', dropzone: 'bg-[var(--mustard-dim)]' },
  Run: { bg: 'bg-[var(--green-dim)]', border: 'border-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', dropzone: 'bg-[var(--green-dim)]' },
};

// ============================================================================
// Sortable Item Component
// ============================================================================

interface SortableItemProps {
  item: PyramidItem;
  onEdit: () => void;
  onDelete: () => void;
  isOverlay?: boolean;
}

function SortableItem({ item, onEdit, onDelete, isOverlay }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative p-2 bg-[var(--bg-raised)] border rounded shadow-sm text-sm flex items-start gap-2 
        ${isDragging ? 'border-[var(--slate)] shadow-lg' : 'border-[var(--rule)] hover:border-[var(--slate)]'}
        ${isOverlay ? 'shadow-xl ring-2 ring-blue-400' : ''}`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-grab active:cursor-grabbing flex-shrink-0"
        title="Drag to move"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[var(--text-pri)] truncate">{item.name}</div>
        {item.description && (
          <div className="text-[var(--text-ter)] text-xs mt-0.5 line-clamp-2">
            {item.description}
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-1 hover:bg-[var(--slate-dim)] rounded"
          title="Edit"
        >
          <Edit2 className="h-3 w-3 text-[var(--text-ter)]" />
        </button>
        <button
          onClick={onDelete}
          className="p-1 hover:bg-[var(--coral-dim)] rounded"
          title="Delete"
        >
          <Trash2 className="h-3 w-3 text-[var(--coral)]" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Droppable Cell Component
// ============================================================================

interface DroppableCellProps {
  cellId: string;
  timeHorizon: TimeHorizon;
  category: Category;
  items: PyramidItem[];
  onEditItem: (item: PyramidItem) => void;
  onDeleteItem: (itemId: string) => void;
  isOver: boolean;
}

function DroppableCell({ 
  cellId, 
  timeHorizon, 
  category, 
  items, 
  onEditItem, 
  onDeleteItem,
  isOver,
}: DroppableCellProps) {
  const { setNodeRef, isOver: isDirectlyOver } = useDroppable({ id: cellId });
  const colors = TIME_HORIZON_COLORS[timeHorizon];
  
  const sortedItems = useMemo(() => 
    [...items].sort((a, b) => a.rank - b.rank),
    [items]
  );

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[80px]  p-2 transition-colors border-2 border-dashed
        ${isOver || isDirectlyOver 
          ? `${colors.dropzone} border-[var(--rule)]` 
          : 'bg-[var(--bg-raised)]/50 border-transparent hover:border-[var(--rule)]'}`}
    >
      <div 
        className="text-xs font-medium text-[var(--text-sec)] text-center mb-2 leading-tight line-clamp-3 min-h-[2.5em]" 
        title={category}
        style={{ wordBreak: 'break-word', hyphens: 'auto' }}
      >
        {category}
      </div>
      
      <SortableContext items={sortedItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {sortedItems.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              onEdit={() => onEditItem(item)}
              onDelete={() => onDeleteItem(item.id)}
            />
          ))}
          {sortedItems.length === 0 && (
            <div className="text-xs text-[var(--text-ter)] text-center italic py-4">
              Drop items here
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

// ============================================================================
// Main Pyramid View Component
// ============================================================================

export function PyramidView({
  items,
  categories,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onUpdateRank,
  onUpdateCategories,
}: PyramidViewProps) {
  // State
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemCategory, setNewItemCategory] = useState<Category>(categories[0] || 'Other Priorities');
  const [newItemTimeHorizon, setNewItemTimeHorizon] = useState<TimeHorizon>('Run');
  const [editingItem, setEditingItem] = useState<PyramidItem | null>(null);
  const [showSwimLaneEditor, setShowSwimLaneEditor] = useState(false);
  const [newSwimLaneName, setNewSwimLaneName] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overCellId, setOverCellId] = useState<string | null>(null);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get active item for drag overlay
  const activeItem = useMemo(() => 
    items.find(item => item.id === activeId),
    [items, activeId]
  );

  // Group items by cell
  const getItemsForCell = (timeHorizon: TimeHorizon, category: Category) => {
    return items.filter(
      item => item.timeHorizon === timeHorizon && item.category === category
    );
  };

  // Parse cell ID to get timeHorizon and category
  const parseCellId = (cellId: string): { timeHorizon: TimeHorizon; category: Category } | null => {
    const [timeHorizon, ...categoryParts] = cellId.split('|');
    const category = categoryParts.join('|') as Category;
    if (TIME_HORIZONS.includes(timeHorizon as TimeHorizon) && categories.includes(category)) {
      return { timeHorizon: timeHorizon as TimeHorizon, category };
    }
    return null;
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag over
  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (over) {
      // Check if we're over a cell (cell IDs contain '|')
      if (typeof over.id === 'string' && over.id.includes('|')) {
        setOverCellId(over.id);
      } else {
        // Over an item - find which cell that item belongs to
        const overItem = items.find(i => i.id === over.id);
        if (overItem) {
          setOverCellId(`${overItem.timeHorizon}|${overItem.category}`);
        }
      }
    } else {
      setOverCellId(null);
    }
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverCellId(null);

    if (!over) return;

    const activeItemData = items.find(item => item.id === active.id);
    if (!activeItemData) return;

    // Determine target cell
    let targetCell: { timeHorizon: TimeHorizon; category: Category } | null = null;

    if (typeof over.id === 'string' && over.id.includes('|')) {
      // Dropped on a cell
      targetCell = parseCellId(over.id);
    } else {
      // Dropped on another item
      const overItem = items.find(item => item.id === over.id);
      if (overItem) {
        targetCell = { timeHorizon: overItem.timeHorizon, category: overItem.category };
      }
    }

    if (!targetCell) return;

    const { timeHorizon: targetTimeHorizon, category: targetCategory } = targetCell;

    // Check if moving to a different cell
    const isSameCell = activeItemData.timeHorizon === targetTimeHorizon && 
                       activeItemData.category === targetCategory;

    if (isSameCell) {
      // Reordering within the same cell
      const cellItems = getItemsForCell(targetTimeHorizon, targetCategory)
        .sort((a, b) => a.rank - b.rank);
      
      const activeIndex = cellItems.findIndex(i => i.id === active.id);
      const overIndex = typeof over.id === 'string' && over.id.includes('|') 
        ? cellItems.length // Dropped on cell = move to end
        : cellItems.findIndex(i => i.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        // Recalculate ranks
        const newItems = [...cellItems];
        const [movedItem] = newItems.splice(activeIndex, 1);
        newItems.splice(overIndex, 0, movedItem);
        
        newItems.forEach((item, index) => {
          onUpdateRank(item.id, index + 1);
        });
      }
    } else {
      // Moving to a different cell
      onUpdateItem(activeItemData.id, {
        timeHorizon: targetTimeHorizon,
        category: targetCategory,
        rank: getItemsForCell(targetTimeHorizon, targetCategory).length + 1,
      });
    }
  };

  // Add item handler
  const handleAddItem = () => {
    if (newItemName.trim()) {
      onAddItem(
        newItemName.trim(),
        newItemDescription.trim() || undefined,
        newItemCategory,
        newItemTimeHorizon
      );
      setNewItemName('');
      setNewItemDescription('');
      setShowAddItem(false);
    }
  };

  // Swim lane handlers
  const handleAddSwimLane = () => {
    if (newSwimLaneName.trim() && onUpdateCategories) {
      const newCategory = newSwimLaneName.trim() as Category;
      if (!categories.includes(newCategory)) {
        onUpdateCategories([...categories, newCategory]);
        setNewSwimLaneName('');
      }
    }
  };

  const handleRemoveSwimLane = (category: Category) => {
    if (onUpdateCategories && categories.length > 1) {
      onUpdateCategories(categories.filter(c => c !== category));
    }
  };

  return (
    <div className="w-full">
      {/* Controls */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddItem(!showAddItem)}
            className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
          {onUpdateCategories && (
            <button
              onClick={() => setShowSwimLaneEditor(!showSwimLaneEditor)}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Swim Lanes ({categories.length})
            </button>
          )}
        </div>
        <div className="text-sm text-[var(--text-ter)]">
          {items.length} item{items.length !== 1 ? 's' : ''} • Drag to move
        </div>
      </div>

      {/* Add Item Form */}
      {showAddItem && (
        <div className="mb-6 p-4 bg-[var(--surface)] border border-[var(--rule)]">
          <h3 className="text-lg font-semibold mb-3">Add New Priority Item</h3>
          <div className="space-y-3">
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Item name..."
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <textarea
              value={newItemDescription}
              onChange={(e) => setNewItemDescription(e.target.value)}
              placeholder="Description (optional)..."
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
            />
            
            {/* Cell Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Time Horizon (Row)
                </label>
                <select
                  value={newItemTimeHorizon}
                  onChange={(e) => setNewItemTimeHorizon(e.target.value as TimeHorizon)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TIME_HORIZONS.map((th) => (
                    <option key={th} value={th}>{TIME_HORIZON_LABELS[th]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Swim Lane (Column)
                </label>
                <select
                  value={newItemCategory}
                  onChange={(e) => setNewItemCategory(e.target.value as Category)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleAddItem}
                disabled={!newItemName.trim()}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)]"
              >
                Add to {TIME_HORIZON_LABELS[newItemTimeHorizon]} / {newItemCategory}
              </button>
              <button
                onClick={() => {
                  setShowAddItem(false);
                  setNewItemName('');
                  setNewItemDescription('');
                }}
                className="px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--rule-bright)]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Swim Lane Editor */}
      {showSwimLaneEditor && (
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-[var(--text-sec)]">Manage Swim Lanes (Columns)</h3>
            <button
              onClick={() => setShowSwimLaneEditor(false)}
              className="p-1 text-[var(--text-sec)] hover:bg-[var(--slate-dim)] rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-[var(--text-sec)] mb-3">
            Customize the strategic theme columns for your pyramid
          </p>
          <div className="space-y-2 mb-3">
            {categories.map((category) => (
              <div key={category} className="flex items-center gap-2 p-2 bg-[var(--bg-raised)] rounded border border-[var(--slate)]">
                <span className="flex-1 text-[var(--text-sec)]">{category}</span>
                {categories.length > 1 && (
                  <button
                    onClick={() => handleRemoveSwimLane(category)}
                    className="p-1 text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                    title="Remove swim lane"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSwimLaneName}
              onChange={(e) => setNewSwimLaneName(e.target.value)}
              placeholder="New swim lane name..."
              className="flex-1 px-3 py-2 border border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onKeyDown={(e) => e.key === 'Enter' && handleAddSwimLane()}
            />
            <button
              onClick={handleAddSwimLane}
              disabled={!newSwimLaneName.trim()}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)]"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Pyramid with Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-col items-center space-y-3">
          {TIME_HORIZONS.map((timeHorizon, horizonIndex) => {
            const colors = TIME_HORIZON_COLORS[timeHorizon];
            // Pyramid widths: top is narrower, bottom is wider
            const widthPercent = 50 + (horizonIndex * 25); // 50%, 75%, 100%

            return (
              <div
                key={timeHorizon}
                className={`${colors.bg} ${colors.border} border-2  p-4 transition-all shadow-sm`}
                style={{ width: `${widthPercent}%`, minWidth: '400px' }}
              >
                {/* Tier Header */}
                <div className="text-center mb-3 pb-2 border-b border-current/10">
                  <h3 className={`text-lg font-bold ${colors.text}`}>
                    {TIME_HORIZON_LABELS[timeHorizon]}
                  </h3>
                  <p className="text-xs text-[var(--text-ter)]">{TIME_HORIZON_DESCRIPTIONS[timeHorizon]}</p>
                </div>

                {/* Cells Grid */}
                <div
                  className="grid gap-3"
                  style={{ gridTemplateColumns: `repeat(${categories.length}, 1fr)` }}
                >
                  {categories.map((category) => {
                    const cellId = `${timeHorizon}|${category}`;
                    const cellItems = getItemsForCell(timeHorizon, category);
                    const isOver = overCellId === cellId;

                    return (
                      <DroppableCell
                        key={cellId}
                        cellId={cellId}
                        timeHorizon={timeHorizon}
                        category={category}
                        items={cellItems}
                        onEditItem={setEditingItem}
                        onDeleteItem={onDeleteItem}
                        isOver={isOver}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeItem ? (
            <SortableItem
              item={activeItem}
              onEdit={() => {}}
              onDelete={() => {}}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Legend */}
      <div className="mt-6 text-center text-xs text-[var(--text-ter)]">
        <span className="inline-block w-3 h-3 rounded bg-[var(--slate-dim)] border border-[var(--slate)] mr-1"></span> Create the Future
        <span className="mx-3">|</span>
        <span className="inline-block w-3 h-3 rounded bg-[var(--mustard-dim)] border border-[var(--mustard)] mr-1"></span> Reinforce Foundation
        <span className="mx-3">|</span>
        <span className="inline-block w-3 h-3 rounded bg-[var(--green-dim)] border border-[var(--green)] mr-1"></span> Get House in Order
      </div>

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-raised)] p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Edit Item</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name</label>
                <input
                  type="text"
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--rule)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
                <textarea
                  value={editingItem.description || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--rule)] min-h-[80px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Swim Lane</label>
                <select
                  value={editingItem.category}
                  onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value as Category })}
                  className="w-full px-3 py-2 border border-[var(--rule)]"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Time Horizon</label>
                <select
                  value={editingItem.timeHorizon}
                  onChange={(e) => setEditingItem({ ...editingItem, timeHorizon: e.target.value as TimeHorizon })}
                  className="w-full px-3 py-2 border border-[var(--rule)]"
                >
                  {TIME_HORIZONS.map((th) => (
                    <option key={th} value={th}>{TIME_HORIZON_LABELS[th]}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onUpdateItem(editingItem.id, editingItem);
                    setEditingItem(null);
                  }}
                  className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingItem(null)}
                  className="flex-1 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--rule-bright)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PyramidView;